import { Visibility, VisibilityOff } from '@mui/icons-material';
import { CircularProgress, Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminLogin,
  requestAdminPasswordReset,
  resetAdminPassword,
  validateAdminPasswordResetOtp,
} from '../../../api/axios';

const defaultTheme = createTheme();

export default function SignInSide() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetStep, setResetStep] = useState('email');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);

  // "https://webback.opencurtainscasting.com/admin/login",
  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const formData = {
      email: data.get('email'),
      password: data.get('password'),
    };

    setError('');
    setEmailError('');
    setPasswordError('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      setEmailError('Email is required');
    } else if (!emailRegex.test(formData.email)) {
      setEmailError('Email is not a valid format');
    }

    if (!formData.password) {
      setPasswordError('Password is required');
    }

    if (
      formData.email &&
      emailRegex.test(formData.email) &&
      formData.password
    ) {
      setIsLoading(true);
      try {
        try {
          const responseData = await adminLogin(formData);
          const token = responseData.token;

          localStorage.setItem('accessToken', token);

          setSnackbarMessage('Login successful!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);

          setTimeout(() => {
            navigate('/dd');
          }, 2000);
        } catch (error) {
          setError(error.response?.data?.message || 'Login failed');
        }
      } catch (error) {
        // console.log('error: ', error);
        console.error('Error during login:', error.message);
        setError('An error occurred during login');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleChange = (event) => {
    setEmailError('');
    setPasswordError('');
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleForgotPasswordSubmit = async () => {
    const email = resetEmail.trim();
    if (!email) {
      setError('Please enter your registered admin email address.');
      return;
    }

    setResetLoading(true);
    setError('');

    try {
      await requestAdminPasswordReset(email);
      setSnackbarMessage('A password reset OTP has been sent to your email.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setResetStep('otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to send the reset OTP.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleOtpValidation = async () => {
    const email = resetEmail.trim();

    if (!email || !resetOtp.trim()) {
      setError('Please enter the OTP sent to your email.');
      return;
    }

    setResetLoading(true);
    setError('');

    try {
      await validateAdminPasswordResetOtp(email, resetOtp);
      setResetStep('password');
      setSnackbarMessage('OTP verified. Create your new password.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to verify the OTP.');
    } finally {
      setResetLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    const email = resetEmail.trim();
    const password = resetPassword.trim();

    if (!password || !resetConfirmPassword.trim()) {
      setError('Please enter and confirm your new password.');
      return;
    }

    if (password !== resetConfirmPassword.trim()) {
      setError('Passwords do not match.');
      return;
    }

    setResetLoading(true);
    setError('');

    try {
      await resetAdminPassword({ email, enteredOTP: resetOtp, newPassword: password });
      setSnackbarMessage('Your password has been reset successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setShowForgotPassword(false);
      setResetEmail('');
      setResetPassword('');
      setResetConfirmPassword('');
      setResetOtp('');
      setResetStep('email');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to reset your password.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box
        component='main'
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CssBaseline />

        <Box
          item
          xs={12}
          sm={12}
          component={Paper}
          elevation={6}
          square
          sx={{
            padding: 4,
            maxWidth: ' 50%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <img
              alt='Open Curtains Casting'
              src={`../../assets/user.png`}
              style={{ width: '40%', height: 'auto', marginBottom: '20px' }}
            />

            <Typography
              component='h1'
              variant='h5'
              sx={{ mb: 2, mt: 3, fontWeight: 'bold' }}
            >
              Sign in
            </Typography>

            <Box
              component='form'
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 1 }}
            >
              {!showForgotPassword && (
                <>
                  <TextField
                    margin='normal'
                    required
                    fullWidth
                    id='email'
                    label='Email Address'
                    name='email'
                    autoComplete='email'
                    autoFocus
                    error={!!emailError}
                    helperText={emailError}
                    onChange={handleChange}
                  />
                  <TextField
                    margin='normal'
                    required
                    fullWidth
                    name='password'
                    label='Password'
                    type={showPassword ? 'text' : 'password'}
                    id='password'
                    autoComplete='current-password'
                    error={!!passwordError}
                    helperText={passwordError}
                    onChange={handleChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge='end'
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}

              {!showForgotPassword && (
                <Button
                  type='submit'
                  fullWidth
                  variant='contained'
                  sx={{ mt: 3, mb: 2, height: 56 }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Sign In'
                  )}
                </Button>
              )}

              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Button
                  variant='text'
                  type='button'
                  onClick={() => {
                    setShowForgotPassword((prev) => !prev);
                    setError('');
                    setResetStep('email');
                  }}
                  sx={{ textTransform: 'none', color: '#6E0506', fontWeight: 600 }}
                >
                  Forgot Password?
                </Button>
              </Box>

              {showForgotPassword && (
                <Box sx={{ mt: 2 }}>
                  {resetStep === 'email' && (
                    <>
                      <TextField
                        margin='dense'
                        fullWidth
                        label='Registered Admin Email'
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        autoComplete='email'
                      />
                      <Button
                        fullWidth
                        variant='contained'
                        type='button'
                        onClick={handleForgotPasswordSubmit}
                        disabled={resetLoading}
                        sx={{ mt: 2, height: 48, backgroundColor: '#6E0506' }}
                      >
                        {resetLoading ? <CircularProgress size={20} color='inherit' /> : 'Send OTP'}
                      </Button>
                    </>
                  )}

                  {resetStep === 'otp' && (
                    <>
                      <TextField
                        margin='dense'
                        fullWidth
                        label='OTP'
                        value={resetOtp}
                        onChange={(e) => setResetOtp(e.target.value)}
                      />
                      <Button
                        fullWidth
                        variant='contained'
                        type='button'
                        onClick={handleOtpValidation}
                        disabled={resetLoading}
                        sx={{ mt: 2, height: 48, backgroundColor: '#6E0506' }}
                      >
                        {resetLoading ? <CircularProgress size={20} color='inherit' /> : 'Verify OTP'}
                      </Button>
                    </>
                  )}

                  {resetStep === 'password' && (
                    <>
                      <TextField
                        margin='dense'
                        fullWidth
                        label='New Password'
                        type={showResetPassword ? 'text' : 'password'}
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowResetPassword(!showResetPassword)}
                                edge="end"
                              >
                                {showResetPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        margin='dense'
                        fullWidth
                        label='Confirm New Password'
                        type={showResetConfirmPassword ? 'text' : 'password'}
                        value={resetConfirmPassword}
                        onChange={(e) => setResetConfirmPassword(e.target.value)}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  setShowResetConfirmPassword(!showResetConfirmPassword)
                                }
                                edge="end"
                              >
                                {showResetConfirmPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        fullWidth
                        variant='contained'
                        type='button'
                        onClick={handlePasswordReset}
                        disabled={resetLoading}
                        sx={{ mt: 2, height: 48, backgroundColor: '#6E0506' }}
                      >
                        {resetLoading ? <CircularProgress size={20} color='inherit' /> : 'Reset Password'}
                      </Button>
                    </>
                  )}
                </Box>
              )}

              {error && (
                <Typography variant='body2' color='error' sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ borderRadius: 10 }}
        >
          <MuiAlert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
